import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { QuickAnnotatePanel } from "./QuickAnnotatePanel";
import { GlassCard } from "./GlassCard";
import { scanApi } from "../api/scan";
import { useAppStore } from "../store/useAppStore";
import {
  newAnnotationNote,
  parseAnnotationNotes,
  serializeAnnotationNotes,
  type AnnotationNote,
} from "../utils/annotationNotes";
import { markLabel, CONTACT_MARK_TONE_CLASS, CONTACT_MARK_PRESETS } from "../utils/contactMarks";

type ScanNotesSectionProps = {
  eventId: string;
  /** Existing scan row id; if missing, resolveHit finds one. */
  scanId?: string;
  /** Raw notes string from scan (JSON array or legacy text). */
  rawNotes?: string | null;
  tags?: string[];
  /**
   * Optional company id — only for company/booth cards.
   * Never pass for representatives/products (leaks notes across colleagues).
   */
  companyId?: string;
  placeholder?: string;
  /** Find scan id when not provided (e.g. by visitor / rep target). */
  resolveScanId?: () => Promise<string | undefined>;
  onSaved?: (next: { scanId: string; notes: string; tags: string[] }) => void;
};

export function ScanNotesSection({
  eventId,
  scanId: scanIdProp,
  rawNotes,
  tags: tagsProp = [],
  companyId,
  placeholder,
  resolveScanId,
  onSaved,
}: ScanNotesSectionProps) {
  const { t } = useTranslation();
  const { updateScan, updateSavedContact, user } = useAppStore();
  const [scanId, setScanId] = useState<string | undefined>(scanIdProp);
  const [noteEntries, setNoteEntries] = useState<AnnotationNote[]>(() =>
    parseAnnotationNotes(rawNotes, tagsProp),
  );

  useEffect(() => {
    setScanId(scanIdProp);
  }, [scanIdProp]);

  useEffect(() => {
    setNoteEntries(parseAnnotationNotes(rawNotes, tagsProp));
    // tagsProp array identity is unstable; rawNotes is the source of truth
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawNotes]);

  return (
    <GlassCard variant="default" padding="md">
      {noteEntries.length > 0 ? (
        <div className="space-y-2 mb-3">
          {noteEntries.map((note) => (
            <div
              key={note.id}
              className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2.5"
            >
              {note.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {note.tags.map((tagId) => {
                    const preset = CONTACT_MARK_PRESETS.find((p) => p.id === tagId);
                    const tone = preset
                      ? CONTACT_MARK_TONE_CLASS[preset.tone]
                      : CONTACT_MARK_TONE_CLASS.slate;
                    return (
                      <span
                        key={tagId}
                        className={`px-2 py-0.5 rounded-full border text-[11px] ${tone.on}`}
                      >
                        {markLabel(tagId, t)}
                      </span>
                    );
                  })}
                </div>
              ) : null}
              {note.text ? (
                <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{note.text}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <QuickAnnotatePanel
        key={`${eventId}-${scanId ?? "pending"}-composer`}
        initialNotes=""
        initialTags={[]}
        title={t("annotate.title")}
        placeholder={placeholder ?? t("annotate.placeholder")}
        saveLabel={t("annotate.save")}
        onSave={async (values) => {
          let hitId = scanId;
          if (!hitId && resolveScanId) {
            hitId = await resolveScanId();
          }
          if (!hitId) {
            toast.error(t("annotate.save_failed"));
            throw new Error("scan_not_found");
          }
          const nextNotes = [...noteEntries, newAnnotationNote(values.notes, values.tags)];
          const serialized = serializeAnnotationNotes(nextNotes);
          const allTags = [...new Set(nextNotes.flatMap((n) => n.tags))];
          await scanApi.updateNotes(eventId, hitId, {
            notes: serialized,
            tags: allTags,
          });
          updateScan(hitId, { notes: serialized, tags: allTags });
          if (companyId) {
            updateSavedContact(companyId, {
              notes: serialized,
              tags: allTags,
              eventId,
              userId: user.id,
            });
          }
          setScanId(hitId);
          setNoteEntries(nextNotes);
          onSaved?.({ scanId: hitId, notes: serialized, tags: allTags });
          toast.success(t("annotate.saved"));
        }}
      />
    </GlassCard>
  );
}
